<script lang="ts">
  /**
   * One seat in a lobby: a presence dot (here, or away), the player's picture
   * when the game passes one (TwinTen ADR 0073), the player's name, and what
   * is true of the seat in small capitals after it: this device's seat
   * ("You"), a computer player ("Bot"), a free-form tag ("Invited · not yet
   * here"), and whether they are ready. A seat nobody has taken is a row too
   * (`empty`), greyed, so the lobby shows who it is waiting for.
   *
   * With `onopen` the picture and the name are one button, the player's
   * profile, named `openLabel` (the name by default).
   *
   * A list item: render it inside a `<ul>` (`LobbyTemplate` does).
   *
   * Test id: `testid` on the row (no default); `data-ready` carries the ready
   * state (`true`, `false`, absent) for a test to read; `openTestid` on the
   * button.
   */
  import { shellText } from '../i18n.svelte.js';
  import type { LobbySeat } from '../templates/playTypes.js';
  import Avatar from './Avatar.svelte';
  import Icon from './Icon.svelte';

  interface Props extends Omit<LobbySeat, 'key'> {
    /** The words for this device's seat. Default the shell's `seatYou` ("You"). */
    readonly youLabel?: string | undefined;
    /** The words for a computer player. Default the shell's `seatBot` ("Bot"). */
    readonly botLabel?: string | undefined;
    /** The words for a ready seat. Default the shell's `seatReady` ("Ready"). */
    readonly readyLabel?: string | undefined;
    /** The words for a seat that is not ready yet. Default `seatNotReady` ("Not ready"). */
    readonly notReadyLabel?: string | undefined;
  }
  const {
    name,
    you = false,
    connected = true,
    ready,
    bot = false,
    badge,
    empty = false,
    avatar,
    onopen,
    openLabel,
    openTestid,
    testid,
    youLabel,
    botLabel,
    readyLabel,
    notReadyLabel,
  }: Props = $props();

  // The shell's own words unless the game passes its own (section 5).
  const words = $derived({
    you: youLabel ?? shellText('seatYou'),
    bot: botLabel ?? shellText('seatBot'),
    ready: readyLabel ?? shellText('seatReady'),
    notReady: notReadyLabel ?? shellText('seatNotReady'),
  });
  const tag = $derived(badge ?? (you ? words.you : undefined));
  const showAvatar = $derived(avatar !== undefined && !empty);
</script>

{#snippet who()}
  {#if showAvatar}
    <Avatar {name} src={avatar} size="sm" />
  {/if}
  <span class="ui-seat-name">{name}</span>
{/snippet}

<li
  class="ui-seat"
  class:ui-seat--empty={empty}
  data-testid={testid}
  data-ready={ready === undefined ? undefined : String(ready)}
>
  <span class="ui-seat-dot" class:ui-seat-dot--away={!connected || empty} aria-hidden="true"></span>
  {#if onopen !== undefined && !empty}
    <button
      type="button"
      class="ui-seat-who ui-seat-open"
      aria-label={openLabel ?? name}
      data-testid={openTestid}
      onclick={onopen}
    >
      {@render who()}
    </button>
  {:else}
    <span class="ui-seat-who">{@render who()}</span>
  {/if}
  {#if bot}
    <span class="ui-seat-tag ui-seat-tag--bot">{words.bot}</span>
  {/if}
  {#if tag !== undefined}
    <span class="ui-seat-tag">{tag}</span>
  {/if}
  {#if ready !== undefined}
    <span class="ui-seat-tag" class:ui-seat-tag--ready={ready}>
      {#if ready}<Icon name="check" size={12} color="var(--ui-presence-online)" />{/if}
      {ready ? words.ready : words.notReady}
    </span>
  {/if}
</li>

<style>
  .ui-seat {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    min-height: 2.75rem;
    box-sizing: border-box;
    padding: 0.75rem 1rem;
    border-radius: var(--ui-radius-md);
    background: var(--ui-sunken);
    /* A hairline inside the edge: on a black page the sunken step is the
       page itself (1.4.0), and the seat must still read as a slot. */
    outline: 1px solid var(--ui-line);
    outline-offset: -1px;
    color: var(--ui-ink);
    font: var(--ui-text-body);
    font-family: var(--ui-font);
    font-weight: 700;
  }

  .ui-seat--empty {
    opacity: 0.6;
  }

  .ui-seat-dot {
    flex: 0 0 auto;
    width: 0.6rem;
    height: 0.6rem;
    border-radius: 50%;
    background: var(--ui-presence-online);
  }

  .ui-seat-dot--away {
    background: var(--ui-presence-away);
  }

  .ui-seat-who {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }

  /* The button takes over the row's own vertical padding (a negative margin,
     the same padding inside), so it is the row's full height — at least a
     44px target (2.75rem) — and the seat is no taller with it than without. */
  .ui-seat-open {
    min-height: 2.75rem;
    margin: -0.75rem 0;
    padding: 0.75rem 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .ui-seat-open:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
    border-radius: var(--ui-radius-sm);
  }

  .ui-seat-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ui-seat-tag {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    font: var(--ui-text-eyebrow);
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--ui-ink-2);
  }

  .ui-seat-tag--bot {
    padding: 0.1rem 0.45rem;
    border-radius: var(--ui-radius-pill);
    background: var(--ui-surface-2);
    color: var(--ui-ink);
  }

  /* Ready reads in ink, a step louder than the quiet tags (presence green is
     gated as text on cards and sheets, not on the sunken seat). */
  .ui-seat-tag--ready {
    color: var(--ui-ink);
  }
</style>
